<script lang="ts" setup>
import { PropType } from 'vue';
import { BalanceOverview as BalanceOverviewModel } from '@/models/balance';
import TaxInformation from './TaxInformation.vue';
import { I18NData } from '@/core/i18n';
import Loading from '@/components/common/Loading.vue';
import Tooltip from '@/components/common/Tooltip.vue';
import { formatCurrency } from '@/filters/filters';
import { TaxInfo } from '@/models/taxes';

const { overview, taxes, type, loading, i18n, hideTitle } = defineProps({
  overview: {
    type: Object as PropType<BalanceOverviewModel>,
    required: true,
  },
  taxes: {
    type: Object as PropType<TaxInfo>,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
  },
  i18n: {
    type: Object as PropType<I18NData>,
    required: true,
  },
  hideTitle: {
    type: Boolean,
    default: false,
  },
});
</script>
<template>
  <section class="section balance-details">
    <h3 v-if="!hideTitle" class="small-loader-header">
      Overview
      <Loading v-if="loading"></Loading>
    </h3>
    <table>
      <thead>
        <tr>
          <th></th>
          <th
            class="balance-details-header invoice-currency"
            v-text="i18n.withoutVAT"
          ></th>
          <th
            class="balance-details-header invoice-currency"
            v-text="i18n.VAT"
          ></th>
          <th
            class="balance-details-header invoice-currency"
            v-text="i18n.total"
          ></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th
            class="balance-details-header invoice-currency"
            v-text="i18n.incoming"
          ></th>
          <td class="balance-detail invoice-currency">
            <span class="currency">{{
              formatCurrency(overview.incoming.without_vat)
            }}</span>
          </td>
          <td class="balance-detail invoice-currency balance-tooltip">
            <Tooltip
              highlight
              text="This is the 'Omzetbelasting' on the VAT declaration."
            >
              <span class="currency">{{
                formatCurrency(overview.incoming.vat)
              }}</span>
            </Tooltip>
          </td>
          <td class="balance-detail invoice-currency balance-tooltip">
            <Tooltip
              highlight
              text="This is the 'bedrag waarover omzetbelasting wordt berekend' on the VAT declaration."
            >
              <span class="currency">{{
                formatCurrency(overview.incoming.total)
              }}</span>
            </Tooltip>
          </td>
        </tr>
        <tr>
          <th class="invoice-currency" v-text="i18n.outgoing"></th>
          <td class="balance-detail invoice-currency">
            <span class="currency">{{
              formatCurrency(overview.outgoing.without_vat)
            }}</span>
          </td>
          <td class="balance-detail invoice-currency balance-tooltip">
            <Tooltip
              highlight
              text="This is the 'Voorbelasting' on the VAT declaration."
            >
              <span class="currency">{{
                formatCurrency(overview.outgoing.vat)
              }}</span>
            </Tooltip>
          </td>
          <td class="balance-detail invoice-currency">
            <span class="currency">{{
              formatCurrency(overview.outgoing.total)
            }}</span>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <th class="invoice-currency" v-text="i18n.balance"></th>
          <td class="balance-detail invoice-currency">
            <span class="currency">{{
              formatCurrency(overview.balance.without_vat)
            }}</span>
          </td>
          <td class="balance-detail invoice-currency">
            <span class="currency">{{
              formatCurrency(overview.balance.vat)
            }}</span>
          </td>
          <td class="balance-detail invoice-currency">
            <span class="currency">{{
              formatCurrency(overview.balance.total)
            }}</span>
          </td>
        </tr>
      </tfoot>
    </table>
    <TaxInformation :taxes="taxes" :i18n="i18n" :type="type"/>
  </section>
</template>
